/**
 * CNC export — the machine profile's SEAT TABLE on the host side (contract
 * v66): what the dialog's "Stone seats" section lists, which cutters each of
 * its pickers may offer, and the edits it makes.
 *
 * Pure (no React, no DOM, no viewer): `scripts/check-cnc-ux.ts` runs it
 * headless.
 *
 * WHY THE TABLE MATTERS. The viewer cuts a stone's seat from the profile's
 * row for that stone. A size the table does not hold is DERIVED from the
 * shop's 2.8 mm row, and a program that uses a derived row is withheld
 * (`seat.derived`) until the shop enters that size as its own — which, before
 * this section, meant editing the profile's JSON by hand.
 *
 * Every edit returns a whole new profile; the dialog sends it through
 * `validateCncProfile` before anything is replaced, the same refusal path as
 * every other profile edit. A row saved here is always the SHOP's
 * (`source: "shop"`): entering a row is the shop confirming it, so the editor
 * asks for that confirmation in so many words.
 */
import type { CncExportResult, CncMachineProfile, CncSeatStandard, CncToolDef } from "../../../shared/api-contract";

/** The viewer's own `SEAT_MATCH_TOL_MM`: a table row within this of a stone's size is that stone's row. */
export const CNC_SEAT_MATCH_TOL_MM = 0.05;

/**
 * The cuts a seat row can be entered for. Round is a plunged seat; the four
 * shaped cuts are the band builder's bare stones, which the viewer pockets
 * round their girdle (a stone-with-prongs is added metal, never a seat).
 */
export const CNC_SEAT_CUTS: readonly { id: string; label: string }[] = [
  { id: "round", label: "Round" },
  { id: "princess", label: "Princess" },
  { id: "oval", label: "Oval" },
  { id: "pear", label: "Pear" },
  { id: "cushion", label: "Cushion" },
];

/** A row's cut: absent means round, as every row before v66. */
export const seatCutOf = (row: Pick<CncSeatStandard, "cut">): string => row.cut ?? "round";
export const isShapedSeat = (row: Pick<CncSeatStandard, "cut">): boolean => seatCutOf(row) !== "round";

export function seatCutLabel(cut: string): string {
  return CNC_SEAT_CUTS.find((c) => c.id === cut)?.label ?? cut.charAt(0).toUpperCase() + cut.slice(1);
}

/** A size or depth as the dialog prints it: at least one decimal (3.0, 2.8), at most three (1.462). */
export function formatSeatMM(v: number): string {
  const t = String(+v.toFixed(3));
  return t.includes(".") ? t : `${t}.0`;
}

/** "the 3.0 mm round stone" / "the 4.9 mm princess" — how a row is named in sentences. */
export function seatRowName(row: Pick<CncSeatStandard, "cut" | "stoneMM">): string {
  return isShapedSeat(row)
    ? `the ${formatSeatMM(row.stoneMM)} mm ${seatCutLabel(seatCutOf(row)).toLowerCase()}`
    : `the ${formatSeatMM(row.stoneMM)} mm round stone`;
}

/** The label of the button a `seat.derived` check offers for one derived row. */
export function enterSeatLabel(row: Pick<CncSeatStandard, "cut" | "stoneMM">): string {
  return isShapedSeat(row)
    ? `Enter the shop's seat for the ${formatSeatMM(row.stoneMM)} mm ${seatCutLabel(seatCutOf(row)).toLowerCase()}`
    : `Enter the shop's seat for ${formatSeatMM(row.stoneMM)} mm`;
}

/** Which picker a tool list is for. */
export type SeatToolRole = "seat" | "pocket" | "drill";

/**
 * The profile's tools a picker may offer — never a tool the profile does not
 * hold:
 *   seat    a round stone's seat cutter: any cutter but a drill, conical first
 *           (the shop's own seat cutter is conical);
 *   pocket  a shaped stone's pocket cutter: flat end mills only (the viewer
 *           refuses anything else);
 *   drill   the light hole: drills that carry a drill cycle.
 * The shop's own tools before assumed ones, then by diameter.
 */
export function seatToolChoices(profile: Pick<CncMachineProfile, "tools">, role: SeatToolRole): CncToolDef[] {
  const fits = (t: CncToolDef) =>
    role === "drill" ? t.kind === "drill" && !!t.drill : role === "pocket" ? t.kind === "flat" : t.kind !== "drill";
  const rank = (t: CncToolDef) => (role === "seat" && t.kind !== "conical" ? 1 : 0);
  return profile.tools
    .filter(fits)
    .sort((a, b) => rank(a) - rank(b) || (a.source === "shop" ? 0 : 1) - (b.source === "shop" ? 0 : 1) || a.diameterMM - b.diameterMM || (a.id < b.id ? -1 : 1));
}

/** How a tool reads in a picker: its name in the program, its size, and whether the shop has confirmed it. */
export function seatToolLabel(t: CncToolDef): string {
  return `${t.label} · Ø${formatSeatMM(t.diameterMM)} mm${t.source === "shop" ? "" : " · not confirmed by the shop"}`;
}

/**
 * The editor's working copy of one row. Numbers stay TEXT while being typed
 * (a half-typed "1." is not a refusal); `rowFromDraft` reads them.
 */
export interface SeatDraft {
  cut: string;
  stoneMM: string;
  seatToolId: string;
  seatDepthMM: string;
  /** Shaped cuts only. */
  clearanceMM: string;
  /** "" ⇒ no light hole. */
  drillToolId: string;
  drillDepthMM: string;
}

const num = (v: number | undefined) => (v === undefined ? "" : String(+v.toFixed(4)));

/** A draft from a row — a table row being edited, or a DERIVED row the shop is asked to confirm. */
export function draftFromRow(row: CncSeatStandard): SeatDraft {
  return {
    cut: seatCutOf(row),
    stoneMM: num(row.stoneMM),
    seatToolId: row.seatToolId,
    seatDepthMM: num(row.seatDepthMM),
    clearanceMM: isShapedSeat(row) ? num(row.clearanceMM) : "",
    drillToolId: row.drillToolId ?? "",
    drillDepthMM: row.drillToolId ? num(row.drillDepthMM) : "",
  };
}

/** An empty draft for a new row of `cut`: the first cutter and drill the pickers offer, numbers to be entered. */
export function emptySeatDraft(profile: Pick<CncMachineProfile, "tools">, cut = "round"): SeatDraft {
  const cutter = seatToolChoices(profile, cut === "round" ? "seat" : "pocket")[0];
  const drill = seatToolChoices(profile, "drill")[0];
  return { cut, stoneMM: "", seatToolId: cutter?.id ?? "", seatDepthMM: "", clearanceMM: "", drillToolId: drill?.id ?? "", drillDepthMM: "" };
}

/**
 * The draft with its cut changed: a round seat and a shaped pocket take
 * different cutters, so a seat cutter the new cut cannot use is swapped for
 * the first one it can.
 */
export function draftWithCut(draft: SeatDraft, cut: string, profile: Pick<CncMachineProfile, "tools">): SeatDraft {
  const choices = seatToolChoices(profile, cut === "round" ? "seat" : "pocket");
  const seatToolId = choices.some((t) => t.id === draft.seatToolId) ? draft.seatToolId : choices[0]?.id ?? "";
  return { ...draft, cut, seatToolId, clearanceMM: cut === "round" ? "" : draft.clearanceMM };
}

/**
 * The row a draft stands for — always the SHOP's — or every reason it is not
 * one yet, in plain words. Checks what a person can get wrong here; the whole
 * profile is still checked by `validateCncProfile` before it is used.
 */
export function rowFromDraft(draft: SeatDraft, profile: Pick<CncMachineProfile, "tools">): { row: CncSeatStandard } | { errors: string[] } {
  const errors: string[] = [];
  const read = (text: string, what: string, lo: number, hi: number, loOpen: boolean): number => {
    const v = Number(text.trim());
    if (text.trim() === "" || !Number.isFinite(v)) errors.push(`${what}: enter a number of mm.`);
    else if (loOpen ? v <= lo : v < lo) errors.push(`${what} must be ${loOpen ? "more than" : "at least"} ${lo} mm.`);
    else if (v > hi) errors.push(`${what} must be at most ${hi} mm.`);
    return v;
  };
  const shaped = draft.cut !== "round";
  const stoneMM = read(draft.stoneMM, "Stone size", 0, 20, true);
  const seatDepthMM = read(draft.seatDepthMM, shaped ? "Pocket depth" : "Seat depth", 0, 10, true);
  const cutters = seatToolChoices(profile, shaped ? "pocket" : "seat");
  if (!cutters.some((t) => t.id === draft.seatToolId)) {
    errors.push(shaped ? "Choose the flat end mill that cuts the pocket." : "Choose the cutter that cuts the seat.");
  }
  const clearanceMM = shaped ? read(draft.clearanceMM, "Clearance round the stone", 0, 1, false) : undefined;
  const drill = draft.drillToolId ? seatToolChoices(profile, "drill").find((t) => t.id === draft.drillToolId) : null;
  if (draft.drillToolId && !drill) errors.push("Choose a drill from the list, or no hole.");
  const drillDepthMM = drill ? read(draft.drillDepthMM, "Hole depth", 0, 10, true) : 0;
  if (errors.length) return { errors };
  return {
    row: {
      stoneMM,
      ...(shaped ? { cut: draft.cut } : {}),
      seatToolId: draft.seatToolId,
      ...(shaped ? { clearanceMM } : {}),
      seatDepthMM,
      drillToolId: drill ? drill.id : null,
      drillDepthMM,
      source: "shop",
    },
  };
}

/** The table row that seats the same stone as `row` (same cut, size within the viewer's match), other than `except`. */
export function findSeatRow(profile: Pick<CncMachineProfile, "seatStandard">, row: Pick<CncSeatStandard, "cut" | "stoneMM">, except: number | null = null): number {
  return profile.seatStandard.findIndex((r, i) =>
    i !== except && seatCutOf(r) === seatCutOf(row) && Math.abs(r.stoneMM - row.stoneMM) <= CNC_SEAT_MATCH_TOL_MM + 1e-9);
}

/**
 * The profile with `row` written at `index` (or appended when null). Refused
 * when another row already seats the same stone — the viewer would take the
 * nearer of the two, so a second row for one stone is never what was meant.
 */
export function withSeatRow(profile: CncMachineProfile, row: CncSeatStandard, index: number | null): { profile: CncMachineProfile } | { error: string } {
  const other = findSeatRow(profile, row, index);
  if (other >= 0) {
    const r = profile.seatStandard[other];
    return { error: `The table already has a row for ${seatRowName(r)} (${formatSeatMM(r.stoneMM)} mm, within ${CNC_SEAT_MATCH_TOL_MM} mm of this one) — edit that row instead.` };
  }
  const seatStandard = index === null ? [...profile.seatStandard, row] : profile.seatStandard.map((r, i) => (i === index ? row : r));
  return { profile: { ...profile, seatStandard } };
}

export function withoutSeatRow(profile: CncMachineProfile, index: number): CncMachineProfile {
  return { ...profile, seatStandard: profile.seatStandard.filter((_, i) => i !== index) };
}

/** The table in reading order: round first, then each cut, smallest stone first. Indices point into `seatStandard`. */
export function seatTableOrder(profile: Pick<CncMachineProfile, "seatStandard">): number[] {
  const cutRank = (row: CncSeatStandard) => {
    const i = CNC_SEAT_CUTS.findIndex((c) => c.id === seatCutOf(row));
    return i < 0 ? CNC_SEAT_CUTS.length : i;
  };
  return profile.seatStandard
    .map((row, i) => ({ row, i }))
    .sort((a, b) => cutRank(a.row) - cutRank(b.row) || seatCutOf(a.row).localeCompare(seatCutOf(b.row)) || a.row.stoneMM - b.row.stoneMM || a.i - b.i)
    .map((x) => x.i);
}

/** The rows a program was planned from that are guesses (`seat.derived`), in the order it lists them. */
export function derivedSeatRows(result: Pick<CncExportResult, "seatStandards"> | null): CncSeatStandard[] {
  return (result?.seatStandards ?? []).filter((r) => r.source !== "shop");
}
